/**
 * Publisher tracking domains — dash data-layer client (feature #136).
 *
 * Thin typed wrapper around `apiClient` for the
 * `/api/v1/publisher-tracking-domains` surface. Mirrors the contract in
 * `apps/api/src/routes/publisher-tracking-domains.ts`:
 *
 *   POST   /                         { affiliateId, domain } → { domain: View }
 *   GET    /:affiliateId             → { affiliate_id, domains: View[] }
 *   POST   /verify/:id               → { domain: View, resolved_values[] }
 *                                      or 422 { domain, expected_value, ... }
 *                                      or 502 { error, detail, domain }
 *   DELETE /:id                      → { deleted: true, id }
 *
 * Keep this module dependency-free beyond `apiClient` — the page component
 * and the unit tests both import from here, and the smoke harness reuses
 * the pure helpers (`isValidFqdn`, `draftSchema`) without pulling React.
 */

import { apiClient, ApiError } from "@/api-client";

// ---------------------------------------------------------------------------
// Status + row shape — match the API's `PublisherTrackingDomainView`.
// ---------------------------------------------------------------------------

export type PublisherTrackingDomainStatus = "pending" | "verified" | "failed";

export const STATUS_LABELS: Record<PublisherTrackingDomainStatus, string> = {
	pending: "Pending",
	verified: "Verified",
	failed: "Failed",
};

export const STATUS_ORDER: readonly PublisherTrackingDomainStatus[] = [
	"pending",
	"verified",
	"failed",
];

export interface PublisherTrackingDomainView {
	id: string;
	affiliate_id: string;
	domain: string;
	verification_token: string;
	verified_at: number | null;
	status: PublisherTrackingDomainStatus;
	created_at: number;
	txt_record_name: string;
	txt_record_value: string;
}

export interface ListDomainsResponse {
	affiliate_id: string;
	domains: PublisherTrackingDomainView[];
}

export interface CreateDomainResponse {
	domain: PublisherTrackingDomainView;
}

export interface VerifyDomainOkResponse {
	domain: PublisherTrackingDomainView;
	resolved_values: string[];
}

export interface VerifyDomainFailedResponse {
	domain: PublisherTrackingDomainView;
	resolved_values: string[];
	expected_value: string;
}

// ---------------------------------------------------------------------------
// Strict FQDN validation — the UI blocks invalid input before POST so the
// operator gets instant feedback. Mirrors the server-side regex in
// publisher-tracking-domains.ts; kept verbatim so tests catch drift.
// ---------------------------------------------------------------------------

const FQDN_RE =
	/^(?=.{1,253}$)(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,63}$/;

export function normalizeDomain(input: string): string {
	return (input ?? "").trim().toLowerCase().replace(/\.$/, "");
}

export function isValidFqdn(domain: string): boolean {
	if (typeof domain !== "string") return false;
	const d = domain.trim();
	if (d.length === 0 || d.length > 253) return false;
	if (d.includes(" ") || d.includes("/") || d.includes(":")) return false;
	if (d.startsWith(".") || d.startsWith("-")) return false;
	return FQDN_RE.test(d.toLowerCase().replace(/\.$/, ""));
}

// ---------------------------------------------------------------------------
// Add-domain form draft + hand-rolled safeParse surface.
// ---------------------------------------------------------------------------

export interface DomainDraft {
	affiliateId: string;
	domain: string;
}

export const EMPTY_DRAFT: DomainDraft = {
	affiliateId: "",
	domain: "",
};

export interface ValidatedDomainDraft {
	affiliateId: string;
	domain: string;
}

export interface ParseOk<T> {
	success: true;
	data: T;
}
export interface ParseErr {
	success: false;
	errors: Record<string, string>;
}
export type ParseResult<T> = ParseOk<T> | ParseErr;

export const domainDraftSchema = {
	safeParse(input: DomainDraft): ParseResult<ValidatedDomainDraft> {
		const errors: Record<string, string> = {};

		const affiliateId = (input.affiliateId ?? "").trim();
		if (affiliateId.length === 0) {
			errors.affiliateId = "Affiliate ID is required.";
		} else if (affiliateId.length > 128) {
			errors.affiliateId = "Affiliate ID must be 128 chars or fewer.";
		}

		const normalized = normalizeDomain(input.domain);
		if (normalized.length === 0) {
			errors.domain = "Domain is required.";
		} else if (!isValidFqdn(normalized)) {
			errors.domain =
				"Enter a fully-qualified domain name (e.g. clicks.brand.com).";
		}

		if (Object.keys(errors).length > 0) return { success: false, errors };
		return {
			success: true,
			data: { affiliateId, domain: normalized },
		};
	},
};

// ---------------------------------------------------------------------------
// Presentation helpers
// ---------------------------------------------------------------------------

export function statusBadgeClass(status: PublisherTrackingDomainStatus): string {
	switch (status) {
		case "verified":
			return "bg-emerald-50 text-emerald-700 border-emerald-200";
		case "failed":
			return "bg-red-50 text-red-700 border-red-200";
		default:
			return "bg-amber-50 text-amber-700 border-amber-200";
	}
}

export function formatVerifiedAt(seconds: number | null): string {
	if (seconds === null || Number.isNaN(seconds)) return "—";
	const d = new Date(seconds * 1000);
	if (Number.isNaN(d.getTime())) return "—";
	return d.toISOString().replace("T", " ").replace(/\.\d{3}Z$/, "Z");
}

/** Sort newest-first, but group by status: pending first (waiting on the
 *  publisher), then verified, then failed. */
export function sortDomains(
	domains: readonly PublisherTrackingDomainView[],
): PublisherTrackingDomainView[] {
	const rank: Record<PublisherTrackingDomainStatus, number> = {
		pending: 0,
		verified: 1,
		failed: 2,
	};
	return domains
		.slice()
		.sort((a, b) => {
			const r = rank[a.status] - rank[b.status];
			if (r !== 0) return r;
			return b.created_at - a.created_at;
		});
}

export function countByStatus(
	domains: readonly PublisherTrackingDomainView[],
): Record<PublisherTrackingDomainStatus, number> {
	const out: Record<PublisherTrackingDomainStatus, number> = {
		pending: 0,
		verified: 0,
		failed: 0,
	};
	for (const d of domains) {
		out[d.status] = (out[d.status] ?? 0) + 1;
	}
	return out;
}

// ---------------------------------------------------------------------------
// Verify-response shaping — turn 200/422/502 bodies into a single discriminant
// the page can switch on.
// ---------------------------------------------------------------------------

export type VerifyOutcome =
	| { kind: "verified"; domain: PublisherTrackingDomainView; resolved: string[] }
	| {
			kind: "mismatch";
			domain: PublisherTrackingDomainView;
			resolved: string[];
			expected: string;
	  }
	| {
			kind: "lookup_error";
			domain: PublisherTrackingDomainView | null;
			detail: string;
	  };

// ---------------------------------------------------------------------------
// Remote operations
// ---------------------------------------------------------------------------

const BASE = "/publisher-tracking-domains";

export async function createDomain(
	payload: ValidatedDomainDraft,
): Promise<PublisherTrackingDomainView> {
	const res = await apiClient.post<CreateDomainResponse>(BASE, {
		affiliateId: payload.affiliateId,
		domain: payload.domain,
	});
	return res.domain;
}

export async function listDomains(
	affiliateId: string,
): Promise<PublisherTrackingDomainView[]> {
	if (!affiliateId.trim()) return [];
	try {
		const res = await apiClient.get<ListDomainsResponse>(
			`${BASE}/${encodeURIComponent(affiliateId.trim())}`,
		);
		return res.domains ?? [];
	} catch (err) {
		if (err instanceof ApiError && err.status === 404) return [];
		throw err;
	}
}

export async function verifyDomain(id: string): Promise<VerifyOutcome> {
	try {
		const ok = await apiClient.post<VerifyDomainOkResponse>(
			`${BASE}/verify/${encodeURIComponent(id)}`,
		);
		return { kind: "verified", domain: ok.domain, resolved: ok.resolved_values };
	} catch (err) {
		if (err instanceof ApiError) {
			const body = err.body as
				| VerifyDomainFailedResponse
				| { error?: string; detail?: string; domain?: PublisherTrackingDomainView }
				| null;
			if (err.status === 422 && body && "expected_value" in body && body.domain) {
				return {
					kind: "mismatch",
					domain: body.domain,
					resolved: body.resolved_values ?? [],
					expected: body.expected_value,
				};
			}
			if (err.status === 502 && body && "error" in body) {
				return {
					kind: "lookup_error",
					domain: (body.domain as PublisherTrackingDomainView) ?? null,
					detail: body.detail ?? err.message,
				};
			}
		}
		throw err;
	}
}

export async function deleteDomain(id: string): Promise<void> {
	try {
		await apiClient.delete(`${BASE}/${encodeURIComponent(id)}`);
	} catch (err) {
		if (err instanceof ApiError && err.status === 404) return;
		throw err;
	}
}

// ---------------------------------------------------------------------------
// TanStack Query keys
// ---------------------------------------------------------------------------

export const publisherTrackingDomainsQueryKeys = {
	all: ["publisher-tracking-domains"] as const,
	byAffiliate: (affiliateId: string) =>
		[...publisherTrackingDomainsQueryKeys.all, affiliateId || "__none__"] as const,
};

// ---------------------------------------------------------------------------
// Apply the result of a verify call to a cached list. Used by the page to
// update the status column optimistically after verify returns.
// ---------------------------------------------------------------------------

export function applyVerifyOutcome(
	domains: readonly PublisherTrackingDomainView[],
	outcome: VerifyOutcome,
): PublisherTrackingDomainView[] {
	if (outcome.kind === "lookup_error" && !outcome.domain) return domains.slice();
	const target =
		outcome.kind === "lookup_error" && outcome.domain
			? outcome.domain
			: outcome.kind !== "lookup_error"
				? outcome.domain
				: null;
	if (!target) return domains.slice();
	return domains.map((d) => (d.id === target.id ? target : d));
}
